import { useCallback, useEffect, useState } from 'react';
import to from 'await-to-js';
import ApiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import type { CampaignReporting, CampaignReportingConfigType } from 'c-sdk';

/**
 * Saved reporting configurations of one type. `campaignType` scopes summary/pca configs to
 * linear or dsp; a dashboard config isn't campaign-scoped, so it is left out there and the
 * filter is omitted entirely.
 */
export const useCampaignReportingConfigs = (
    type: CampaignReportingConfigType,
    campaignType?: string,
) => {
    const { start: listStart, isLoading: isListing } = useAPIClientRequest(
        ApiClient.Entities.CampaignReporting.list,
    );
    const { start: createStart, isLoading: isCreating } = useAPIClientRequest(
        ApiClient.Entities.CampaignReporting.create,
    );
    const { start: updateStart, isLoading: isUpdating } = useAPIClientRequest(
        ApiClient.Entities.CampaignReporting.update,
    );
    const { start: deleteStart } = useAPIClientRequest(ApiClient.Entities.CampaignReporting.delete);

    const [configs, setConfigs] = useState<CampaignReporting[]>([]);

    const refresh = useCallback(async () => {
        const [err, res] = await to(
            listStart({
                perPage: 1000,
                filters: {
                    'filter.type': [type],
                    ...(campaignType ? { 'filter.campaign_type': [campaignType] } : {}),
                },
            }),
        );
        if (!err) {
            setConfigs((((res as any)?.data?.data ?? []) as CampaignReporting[]) ?? []);
        }
    }, [listStart, type, campaignType]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const save = useCallback(
        async (name: string, config: string, id?: number) => {
            const payload = {
                name,
                type,
                config,
                ...(campaignType ? { campaign_type: campaignType } : {}),
            };
            const [err] =
                id != null ? await to(updateStart(id, payload)) : await to(createStart(payload));
            if (!err) await refresh();
            return !err;
        },
        [createStart, updateStart, refresh, type, campaignType],
    );

    const remove = useCallback(
        async (id: number) => {
            const [err] = await to(deleteStart(id));
            if (!err) await refresh();
        },
        [deleteStart, refresh],
    );

    return { configs, refresh, save, remove, isListing, isSaving: isCreating || isUpdating };
};
